import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, ExternalLink, Link2, Loader2, Unlink } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import type { GoogleWorkspaceStatus } from '../../lib/googleWorkspace';
import { GoogleWorkspaceTemplatesCard } from '../../components/google/GoogleWorkspaceTemplatesCard';
import { BulkLinkCampaignsModal } from '../../components/google/BulkLinkCampaignsModal';

export default function GoogleWorkspaceSettingsPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<GoogleWorkspaceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [bulkLinkOpen, setBulkLinkOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<GoogleWorkspaceStatus>('/google-workspace/status');
      setStatus(res.data);
    } catch {
      setMessage('Failed to load Google Workspace status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    if (connected === '1') setMessage('Google account connected successfully.');
    if (error) setMessage(`Connection failed: ${error}`);
  }, [load, searchParams]);

  const handleConnect = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await api.get<{ authUrl: string }>('/google-workspace/oauth/start');
      window.location.href = res.data.authUrl;
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'OAuth is not configured. You can still link Google files by URL on each campaign.';
      setMessage(msg);
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Disconnect Google Workspace from your agency?')) return;
    setBusy(true);
    try {
      await api.delete('/google-workspace/connection');
      await load();
      setMessage('Disconnected.');
    } catch {
      setMessage('Failed to disconnect');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="page-title">Google Workspace</h1>
        <p className="text-glass-text-2 mt-1">
          Connect Google, manage shared templates, and bulk-link files across campaigns.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-accent2" />
            Connection
          </CardTitle>
          <CardDescription>
            Optional for Phase 1 — you can paste Google URLs on any job without connecting. OAuth enables
            automatic Drive folder creation in a later phase.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-accent2" />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                {status?.connected ? (
                  <>
                    <Badge variant="success" className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Connected
                    </Badge>
                    {status.email && <span className="text-sm text-glass-text-2">{status.email}</span>}
                    {status.connectedAt && (
                      <span className="text-xs text-glass-text-3">
                        since {new Date(status.connectedAt).toLocaleDateString()}
                      </span>
                    )}
                  </>
                ) : (
                  <Badge variant="outline">Not connected</Badge>
                )}
                {!status?.configured && (
                  <Badge variant="secondary">OAuth not configured on server</Badge>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {!status?.connected && status?.configured && (
                  <Button onClick={handleConnect} disabled={busy}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Connect Google account
                  </Button>
                )}
                {status?.connected && (
                  <Button variant="outline" onClick={handleDisconnect} disabled={busy}>
                    <Unlink className="h-4 w-4 mr-2" />
                    Disconnect
                  </Button>
                )}
              </div>

              {message && <p className="text-sm text-glass-text-2">{message}</p>}
            </>
          )}
        </CardContent>
      </Card>

      <GoogleWorkspaceTemplatesCard />

      <Card>
        <CardHeader>
          <CardTitle>Bulk link campaigns</CardTitle>
          <CardDescription>
            Copy Google Doc, Slides, and folder links from one campaign to many others at once.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={() => setBulkLinkOpen(true)}>
            Bulk link Google files…
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How to use (no OAuth required)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-glass-text-2 space-y-3">
          <p>
            On any job <strong>Brief</strong> tab, use the Google Workspace panel to paste:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>Google Doc URL — brand campaign brief</li>
            <li>Google Slides URL — storyboard deck</li>
            <li>Google Drive folder URL — shared campaign files</li>
          </ul>
          <p>
            In <strong>Script Workspace</strong>, link a per-KOL storyboard Slides deck on the Storyboard tab.
          </p>
          <a
            href="https://support.google.com/docs/answer/183965"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-accent2 hover:underline"
          >
            Google sharing guide <ExternalLink className="h-3 w-3" />
          </a>
        </CardContent>
      </Card>

      <BulkLinkCampaignsModal isOpen={bulkLinkOpen} onClose={() => setBulkLinkOpen(false)} />
    </div>
  );
}
